/**
 * Google Analytics v4 + Consent Mode v2 helpers.
 *
 * Provides reusable utilities to load GA, manage consent state, and track events.
 * All functions are no-ops when a measurement ID is missing or when running
 * outside the browser.
 */
let scriptLoaded = false;
let gtagInitialized = false;

const CONSENT_FIELDS = ["ad_storage", "analytics_storage", "ad_user_data", "ad_personalization"];

/**
 * Checks if code is executing in a browser context.
 *
 * @returns {boolean} True if window is available
 */
const isBrowser = () => typeof window !== "undefined";

/**
 * Initializes the gtag data layer and loads the GA script with a denied default.
 *
 * @param {string} measurementId - GA4 measurement ID
 * @returns {boolean} True when script load is initiated
 */
export const initGoogleAnalytics = (measurementId) => {
  if (!isBrowser() || !measurementId) return false;
  if (scriptLoaded) return true;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window.gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.onload = () => {
    window.gtag("js", new Date());
    gtagInitialized = true;
  };

  document.head.appendChild(script);
  scriptLoaded = true;
  return true;
};

/**
 * Updates consent state according to user decision.
 *
 * @param {boolean} isGranted - True to grant consent, false to deny
 */
export const updateConsentMode = (isGranted) => {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  const status = isGranted ? "granted" : "denied";

  const payload = CONSENT_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]: status,
    }),
    {}
  );

  window.gtag("consent", "update", payload);
};

/**
 * Configures GA with privacy-first defaults. Safe to call multiple times.
 *
 * @param {string} measurementId - GA4 measurement ID
 */
export const configureAnalytics = (measurementId) => {
  if (!isBrowser() || typeof window.gtag !== "function" || !measurementId) return;

  window.gtag("config", measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
};

/**
 * Tracks a page view event.
 *
 * @param {string} measurementId - GA4 measurement ID
 * @param {Object} options - Page view options
 * @param {string} options.path - Page path
 * @param {string} [options.title] - Page title
 */
export const trackPageView = (measurementId, { path, title }) => {
  if (!isBrowser() || typeof window.gtag !== "function" || !measurementId) return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

/**
 * Tracks a custom GA event.
 *
 * @param {string} eventName - GA event name
 * @param {Object} [params={}] - Additional parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  window.gtag("event", eventName, params);
};

/**
 * Returns whether the GA script has been initialized.
 *
 * @returns {boolean} True if gtag.js was loaded
 */
export const isAnalyticsInitialized = () => gtagInitialized;
